import { Platform } from 'react-native';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

type UtmKey = (typeof UTM_KEYS)[number];
export type UtmProperties = Record<UtmKey, string | undefined>;

const STORAGE_KEY = 'tasktag_utm_first_touch';

function isValidUtmObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Reads UTM params from the current URL and persists to localStorage.
// First-touch: skips silently if at least one known UTM key is already stored.
// Clears and re-captures if the stored value is corrupted, invalid JSON, or
// contains no recognized UTM keys (e.g. {}, { foo: "bar" }).
export function captureUtmParams(): void {
  if (Platform.OS !== 'web') return;
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing !== null) {
      try {
        const parsed = JSON.parse(existing);
        if (
          isValidUtmObject(parsed) &&
          UTM_KEYS.some(
            (k) =>
              typeof parsed[k] === 'string' && (parsed[k] as string).length > 0
          )
        ) {
          return;
        }
      } catch {
        // corrupted — fall through to clear and re-capture
      }
      localStorage.removeItem(STORAGE_KEY);
    }
    const params = new URLSearchParams(window.location.search);
    const utm: Partial<Record<UtmKey, string>> = {};
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) utm[key] = val;
    }
    if (Object.keys(utm).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    }
  } catch {
    // localStorage unavailable (private browsing, quota exceeded) — fail silently
  }
}

// Returns stored first-touch UTM properties.
// All 5 UTM keys are always present; value is undefined when not captured.
// Each value is defensively coerced to string — non-string payloads in storage
// (null, numbers, objects) are treated as absent to avoid leaking into Mixpanel.
export function getStoredUtmProperties(): UtmProperties {
  if (Platform.OS !== 'web') {
    return Object.fromEntries(
      UTM_KEYS.map((k) => [k, undefined])
    ) as UtmProperties;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const raw = stored ? JSON.parse(stored) : {};
    const utm: Record<string, unknown> = isValidUtmObject(raw) ? raw : {};
    return Object.fromEntries(
      UTM_KEYS.map((k) => {
        const v = utm[k];
        return [k, typeof v === 'string' && v.length > 0 ? v : undefined];
      })
    ) as UtmProperties;
  } catch {
    return Object.fromEntries(
      UTM_KEYS.map((k) => [k, undefined])
    ) as UtmProperties;
  }
}

// Returns UTM properties suitable for Mixpanel event tracking.
// All 5 UTM keys are always present as strings. Keys not captured from the URL
// are set to the string 'undefined' so Mixpanel retains the property name in the
// event stream — satisfying the AC that no-UTM signups show utm_source = 'undefined'
// rather than a missing property.
export function getUtmEventProperties(): Record<UtmKey, string> {
  const stored = getStoredUtmProperties();
  return Object.fromEntries(
    UTM_KEYS.map((k) => [k, stored[k] ?? 'undefined'])
  ) as Record<UtmKey, string>;
}
